import { Catalog, EmptyCatalog } from '@/types/Catalog';
import {
    LIVE_AUCTION_ENDED,
    LIVE_AUCTION_OPENED,
    LIVE_AUCTION_STARTED,
    LOAD_CATALOGS_FAIL,
    LOAD_CATALOGS_REQUEST,
    LOAD_CATALOGS_SUCCESS,
    LOAD_CATALOGS_SUCCESS_ACTION,
    LOAD_HOME_DATA_SUCCESS,
    LOAD_LIVE_CATALOG_STATUS_SUCCESS,
    LOAD_UPCOMING_CATALOGS_SUCCESS,
} from './actions';
import { loadBatchEnd, loadBatchStart } from './fetchBatch';
import api from '@/redux/api/catalog';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import { EmptyCoverLot } from '@/types/CoverLot';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import { LOAD_SELLER_ENDED_CATALOGS_SUCCESS } from './sellerEndedCatalogs';
import { LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS } from './sellerUpcomingCatalogs';
import ms from 'ms';
import { PaymentProviders } from '@/enums';
import { safelyEscapeText } from '@/utils/safelyRender';
import { shouldShowLive } from '@/utils/live';
import union from 'lodash/union';

const REDUX_STORE_TIME = ms('30m');
const FETCH_SET_SIZE = 120;

export type CatalogState = {
    // dictionary, key is catalog id, value is the catalog item
    byId: { [id: number]: Catalog };
    // dictionary, key is catalog id, value is the timestamp the catalog was loaded
    loaded: { [id: number]: number };
    // array of catalog ids that are in the middle of being loaded
    loading: number[];
};

/* reducer */
export const DEFAULT_STATE: CatalogState = {
    byId: {},
    loaded: {},
    loading: [],
};

export default function reducer(state: CatalogState = DEFAULT_STATE, action: any = {}): CatalogState {
    let existing: typeof state.byId;
    let loaded: typeof state.loaded;
    let loading: typeof state.loading;
    let time: number;

    switch (action.type) {
        case LOAD_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, action.meta.catalogIds),
            };
        case LOAD_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CATALOGS_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
        case LOAD_SELLER_UPCOMING_CATALOGS_SUCCESS:
        case LOAD_SELLER_ENDED_CATALOGS_SUCCESS:
        case LOAD_UPCOMING_CATALOGS_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (action.payload.catalogs) {
                action.payload.catalogs.forEach((catalog) => {
                    let paymentProvider: number = PaymentProviders.Stripe;
                    if (catalog.highRiskPayments) {
                        paymentProvider = PaymentProviders.Paysafe;
                    } else if (catalog.payrixPayments) {
                        paymentProvider = PaymentProviders.Payrix;
                    }

                    existing[catalog.catalogId] = {
                        paymentProvider,
                        ...catalog,
                        title: safelyEscapeText(catalog.title),
                    };

                    existing[catalog.catalogId].coverLots = [];
                    if (action.payload?.coverLots?.length) {
                        existing[catalog.catalogId].coverLots =
                            action.payload.coverLots.find((cl) => cl?.catalogId === catalog.catalogId)?.coverLots || [];
                    }

                    delete existing[catalog.catalogId].payrixPayments;
                    loaded[catalog.catalogId] = time;
                    loading = difference(loading, [catalog.catalogId]);
                });
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LIVE_AUCTION_ENDED:
            existing = cloneDeep(state.byId);
            existing[action.payload.catalogId].catalogStatus = 'done';
            return {
                ...state,
                byId: existing,
            };
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            // Found a case from dante where the failure was not being transformed correctly.
            if (action.payload?.catalogId) {
                const { catalogId, status } = action.payload;
                existing = cloneDeep(state.byId);
                existing[catalogId] = existing[catalogId] || EmptyCatalog;
                if (status === 'done' && existing[catalogId].catalogStatus !== 'done') {
                    existing[catalogId].catalogStatus = 'done';
                } else if (status === 'live' && existing[catalogId].catalogStatus !== 'done') {
                    existing[catalogId].catalogStatus = 'live';
                }

                return {
                    ...state,
                    byId: existing,
                };
            }

            return state;
        case LIVE_AUCTION_OPENED:
        case LIVE_AUCTION_STARTED:
            if (action.payload?.catalogId) {
                existing = cloneDeep(state.byId);
                existing[action.payload.catalogId] = existing[action.payload.catalogId] || EmptyCatalog;
                existing[action.payload.catalogId].catalogStatus = 'live';
                return {
                    ...state,
                    byId: existing,
                };
            }

            return state;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.catalog;
const idSelector = (state: GlobalState, id) => id;

const catalogsByIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getIsCatalogLoaded = createSelector([loadedSelector, idSelector], (loaded, id) =>
    loaded.hasOwnProperty(id)
);

export const getCatalog = createSelector([catalogsByIdSelector, idSelector], (byId, id) => byId[id] || EmptyCatalog);

export const getCatalogPreview = createSelector(
    [catalogsByIdSelector, idSelector],
    (byId, id) => byId?.[id]?.publishDateTs === 0 && byId?.[id].catalogStatus !== 'done'
);
export const getCatalogPaymentProvider = createSelector(getCatalog, (catalog) => catalog?.paymentProvider);
export const getCatalogCoverLots = createSelector(
    getCatalog,
    (catalog) => catalog?.coverLots || [EmptyCoverLot, EmptyCoverLot, EmptyCoverLot]
);

export const getShouldShowLive: (state: any, selector: any) => any = createSelector(
    [catalogsByIdSelector, idSelector],
    (byId, id) => {
        if (byId[id]) {
            const { catalogStatus, isCatalogOnly, saleStartTs } = byId[id];
            return shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
        }
        return false;
    }
);

export const getAllCatalogs = catalogsByIdSelector;

export const getCatalogIdsForSeller = createSelector([catalogsByIdSelector, idSelector], (byId, id) =>
    Object.keys(byId)
        .map((key) => byId[key])
        .filter((catalog) => catalog?.sellerId === id && catalog?.catalogStatus)
        .map((catalog) => catalog.catalogId)
);

const whichCatalogsNeeded = createSelector(
    [catalogsByIdSelector, loadedSelector, loadingSelector, idSelector],
    (ids, loaded, loading, catalogIds) => {
        const needed: number[] = [];
        catalogIds.forEach((catalogId: number) => {
            if (catalogId) {
                const time = Date.now();
                const diff = time - loaded[catalogId];
                if (diff < REDUX_STORE_TIME) {
                    return false;
                }
            }
            if (!loading.includes(catalogId)) {
                needed.push(catalogId);
            }
        });
        return needed;
    }
);

/* ACTION CREATORS */
const loadCatalogs = (catalogIds: number[], loadingId: string, isFinalFetch: boolean) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: catalogIds,
            type: LOAD_CATALOGS_REQUEST,
        });
        const response = await api.fetchCatalogsByIds({ authToken, catalogIds, deployment });
        dispatch({
            meta: { actionTime: Date.now(), catalogIds },
            payload: response.data,
            type: LOAD_CATALOGS_SUCCESS,
        } as LOAD_CATALOGS_SUCCESS_ACTION);
        if (isFinalFetch) {
            dispatch(loadBatchEnd(loadingId));
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { catalogIds },
            payload: error,
            type: LOAD_CATALOGS_FAIL,
        });
        dispatch(loadBatchEnd(loadingId));
    }
};

const loadCatalogsInSets = (ids, setSize, loadingId) => {
    return (dispatch) => {
        if (ids.length > setSize) {
            return dispatch(loadCatalogs(ids.slice(0, setSize), loadingId, false)).then(() => {
                dispatch(loadCatalogsInSets(ids.slice(setSize), setSize, loadingId));
            });
        }
        return dispatch(loadCatalogs(ids, loadingId, true));
    };
};

export const fetchCatalogsIfNeeded =
    (catalogIds: number[], loadingId: string = 'none') =>
    async (dispatch: Function, getState: Function) => {
        const needed = whichCatalogsNeeded(getState(), catalogIds);
        if (needed.length) {
            dispatch(loadBatchStart(loadingId));
            return dispatch(loadCatalogsInSets(needed, FETCH_SET_SIZE, loadingId));
        }
        return Promise.resolve();
    };
